import React, { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import ReactApexChart from "react-apexcharts"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl, formatPeriodLabel } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const BCG_META = {
	star: { label: "Звёзды", color: "#f0b429", icon: "uil-star", hint: "Высокий рост + высокая доля" },
	cow: { label: "Дойные коровы", color: "#2f80c9", icon: "uil-link", hint: "Низкий рост + высокая доля" },
	question: { label: "Загадки", color: "#9b59b6", icon: "uil-question-circle", hint: "Высокий рост + низкая доля" },
	dog: { label: "Собаки", color: "#e23b3b", icon: "uil-paw", hint: "Низкий рост + низкая доля" },
	new: { label: "Новые", color: "#12b886", icon: "uil-plus-circle", hint: "Не продавались в прошлом периоде — рост не с чем сравнить" },
}

const BCG_ORDER = ["star", "cow", "question", "dog"]

function formatGrowth(value) {
	if (value === null || value === undefined) return "—"
	const number = Number(value)
	return `${number >= 0 ? "+" : ""}${number.toFixed(1).replace(".0", "")}%`
}

function KpiCard({ title, value, sublabel, valueColor = "var(--dark-text-strong, #111827)", icon, iconColor }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body py-3 px-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={mutedStyle}>{title}</div>
					{icon && <i className={`uil ${icon}`} style={{ fontSize: "16px", color: iconColor || "var(--dark-text-muted, #8791a1)" }}></i>}
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "26px", fontWeight: 800, color: valueColor, letterSpacing: "-0.5px" }}>
					{value}
				</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function Matrix({ data }) {
	const products = useMemo(() => data?.products || [], [data])
	const shareThreshold = Number(data?.thresholds?.share || 0)
	const growthThreshold = Number(data?.thresholds?.growth || 0)

	const grouped = useMemo(() => {
		const map = { star: [], cow: [], question: [], dog: [] }
		products.forEach(product => {
			if (map[product.bcg_category]) map[product.bcg_category].push(product)
		})
		return map
	}, [products])

	if (products.filter(product => product.bcg_category !== "new").length === 0) {
		return <div className="text-muted text-center py-5" style={{ minHeight: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>Недостаточно данных для построения матрицы</div>
	}

	const series = BCG_ORDER.map(key => ({
		name: BCG_META[key].label,
		data: grouped[key].map(product => ({ x: product.growth_percent, y: product.share_percent, product })),
	}))

	const options = {
		chart: { type: "scatter", height: 420, toolbar: { show: false }, fontFamily: "inherit", zoom: { enabled: true } },
		colors: BCG_ORDER.map(key => BCG_META[key].color),
		markers: { size: 7, strokeWidth: 0, hover: { sizeOffset: 2 } },
		legend: { position: "top", markers: { radius: 12 } },
		grid: { borderColor: "#eef1f5", strokeDashArray: 4 },
		xaxis: {
			type: "numeric",
			title: { text: "Рост к прошлому периоду, %", style: { color: "#8791a1", fontWeight: 500 } },
			tickAmount: 8,
			labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => `${Math.round(value)}%` },
			axisBorder: { show: false },
			axisTicks: { show: false },
		},
		yaxis: {
			title: { text: "Доля выручки, %", style: { color: "#8791a1", fontWeight: 500 } },
			labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => `${Number(value).toFixed(1)}%` },
		},
		annotations: {
			xaxis: [{ x: growthThreshold, borderColor: "#cbd5e1", strokeDashArray: 4 }],
			yaxis: [{ y: shareThreshold, borderColor: "#cbd5e1", strokeDashArray: 4 }],
		},
		tooltip: {
			custom: ({ seriesIndex, dataPointIndex, w }) => {
				const point = w.config.series[seriesIndex].data[dataPointIndex]
				const product = point?.product || {}
				return `<div style="padding:8px 10px;">
						<div style="font-weight:600;margin-bottom:4px;">${product.product_name || ""}</div>
						<div style="color:#8791a1;font-size:11px;">${product.category_name || ""}</div>
						<div style="margin-top:4px;">Выручка: <b>${formatMoney(product.revenue || 0, 0)}</b></div>
						<div>Доля: <b>${Number(product.share_percent || 0).toFixed(1)}%</b></div>
						<div>Рост: <b>${formatGrowth(product.growth_percent)}</b></div>
					</div>`
			},
		},
	}

	return <ReactApexChart options={options} series={series} type="scatter" height={420} />
}

function NewProducts({ products }) {
	if (products.length === 0) return null
	return (
		<div className="card mt-3" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-1 d-flex align-items-center gap-2">
					<i className={`uil ${BCG_META.new.icon}`} style={{ color: BCG_META.new.color }}></i>
					Новые блюда
				</h5>
				<div style={mutedStyle} className="mb-2">{BCG_META.new.hint}; на матрице не показаны</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0">
						<thead>
							<tr>
								<th className="text-muted fw-semibold">Блюдо</th>
								<th className="text-muted fw-semibold">Категория</th>
								<th className="text-muted fw-semibold text-end">Выручка</th>
								<th className="text-muted fw-semibold text-end">Доля</th>
							</tr>
						</thead>
						<tbody>
							{products.map(product => (
								<tr key={product.product_id}>
									<td style={{ fontWeight: 600 }}>{product.product_name}</td>
									<td className="text-muted">{product.category_name}</td>
									<td className="text-end">{formatMoney(product.revenue, 0)}</td>
									<td className="text-end">{Number(product.share_percent || 0).toFixed(1)}%</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function Bcg() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)

	const filters = useAnalyticsFilters({
		...buildPeriod("30days"),
		pos_id: reduxAccount.pos_id ?? -1,
		order_type_ids: [],
		source_ids: [],
		menu_ids: [],
		category_ids: [],
		min_revenue: 50000,
	}, searchWithFilter)
	const { draft, patch } = filters
	const options = useAnalyticsOptions(["sources", "menus", "categories"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/bcg", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	function exportExcel() {
		if (!data) return
		const rows = (data.products || [])
			.map(product => `<tr><td>${product.product_name}</td><td>${product.menu_name}</td><td>${product.category_name}</td><td>${product.revenue}</td><td>${product.compare_revenue}</td><td>${product.share_percent}%</td><td>${product.growth_percent === null ? "новое" : `${product.growth_percent}%`}</td><td>${BCG_META[product.bcg_category]?.label || ""}</td></tr>`)
			.join("")
		const html = `<table border="1"><thead><tr><th>Блюдо</th><th>Меню</th><th>Категория</th><th>Выручка</th><th>Выручка прошлого периода</th><th>Доля</th><th>Рост</th><th>Квадрант</th></tr></thead><tbody>${rows}</tbody></table>`
		const blob = new Blob([`﻿${html}`], { type: "application/vnd.ms-excel" })
		const link = document.createElement("a")
		link.href = URL.createObjectURL(blob)
		link.download = `bcg-${data.start_date}_${data.end_date}.xls`
		link.click()
		URL.revokeObjectURL(link.href)
	}

	const counts = data?.counts || {}
	const newProducts = (data?.products || []).filter(product => product.bcg_category === "new")
	const networkGrowth = data?.network_growth_percent

	return (
		<>
			<AnalyticsFilterBar
				title="BCG Матрица"
				icon="uil-apps"
				subtitle={data ? `Сравнение с ${formatPeriodLabel(data.compare_start, data.compare_end)}` : "Распределение блюд по доле выручки и динамике роста"}
				filters={filters}
				presets={["7days", "30days", "this_month", "last_month", "custom"]}
				actions={
					<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={exportExcel}>
						<i className="uil uil-file-export me-1"></i>
						Excel
					</button>
				}
			>
				<StandardFields filters={filters} options={options} />
				<FilterField label="Меню">
					<FilterSelect isMulti options={options.menus} allLabel="Все меню" value={draft.menu_ids} onChange={(ids) => patch({ menu_ids: ids })} />
				</FilterField>
				<FilterField label="Категория">
					<FilterSelect isMulti options={options.categories} allLabel="Все категории" value={draft.category_ids} onChange={(ids) => patch({ category_ids: ids })} />
				</FilterField>
				<FilterField label="Мин. выручка блюда">
					<input
						type="number"
						min="0"
						className="form-control"
						style={{ borderRadius: "10px", minHeight: "38px" }}
						value={draft.min_revenue}
						onChange={(event) => patch({ min_revenue: event.target.value === "" ? 0 : Number(event.target.value) })}
					/>
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-2 col-md-4 col-6">
					<KpiCard
						title="Рост сети"
						value={formatGrowth(networkGrowth)}
						sublabel={networkGrowth === null || networkGrowth === undefined ? "в прошлом периоде продаж не было" : "к предыдущему периоду"}
						valueColor={networkGrowth === null || networkGrowth === undefined ? "#111827" : (Number(networkGrowth) >= 0 ? "#1f9d57" : "#d23434")}
					/>
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<KpiCard
						title="Блюд в анализе"
						value={formatMoney(counts.analyzed || 0, 0)}
						sublabel={counts.hidden > 0 ? `скрыто мелких: ${formatMoney(counts.hidden, 0)}` : "все блюда с продажами"}
					/>
				</div>
				{["star", "cow", "question", "dog"].map(key => (
					<div className="col-xl-2 col-md-4 col-6" key={key}>
						<KpiCard
							title={BCG_META[key].label}
							value={formatMoney(counts[{ star: "stars", cow: "cows", question: "questions", dog: "dogs" }[key]] || 0, 0)}
							valueColor={BCG_META[key].color}
							icon={BCG_META[key].icon}
							iconColor={BCG_META[key].color}
						/>
					</div>
				))}
			</div>

			<div className="card" style={cardStyle}>
				<div className="card-body">
					<Matrix data={data} />
					<div className="d-flex flex-wrap gap-4 mt-3 pt-3" style={{ borderTop: "1px solid var(--dark-border, #eef2f6)", fontSize: "12px" }}>
						{["star", "cow", "question", "dog", "new"].map(key => (
							<span key={key}>
								<i className={`uil ${BCG_META[key].icon}`} style={{ color: BCG_META[key].color }}></i> <b>{BCG_META[key].label}</b>
								<span className="text-muted"> — {BCG_META[key].hint}</span>
							</span>
						))}
					</div>
					<div style={{ ...mutedStyle, marginTop: "8px" }}>
						Оси делят по средней доле блюда и по росту всей выручки. Порог «мин. выручка» только скрывает мелкие блюда — расчёт осей по всем блюдам.
						Выручка — по строкам чека с учётом скидки на чек, за вычетом возвратов.
					</div>
				</div>
			</div>

			<NewProducts products={newProducts} />
		</>
	)
}

export default Bcg
